import type { TFunction } from 'react-i18next';
import type { ChatItem, ToolStatus } from './reducer';
import { shortenWorkspacePaths, toolArgsLabel } from './toolLabels';

type ToolItem = Extract<ChatItem, { kind: 'tool' }>;
type AssistantItem = Extract<ChatItem, { kind: 'assistant' }>;

export type ToolGroupMember = ToolItem | AssistantItem;

export type ChatSegment =
  | { kind: 'item'; key: string; item: ChatItem; afterCount: number }
  | {
      kind: 'toolGroup';
      key: string;
      toolName: string;
      members: ToolGroupMember[];
      tools: ToolItem[];
      afterCount: number;
    };

export const groupsEveryTool = (): boolean => true;

const isThinkingOnly = (item: ChatItem): item is AssistantItem =>
  item.kind === 'assistant' && !item.text;

const hasEventsAt = (
  eventsByIndex: ReadonlyMap<number, readonly unknown[]>,
  count: number
) => (eventsByIndex.get(count)?.length ?? 0) > 0;

export const groupToolRuns = (
  items: ChatItem[],
  eventsByIndex: ReadonlyMap<number, readonly unknown[]>,
  groupsTool: (item: ToolItem) => boolean = groupsEveryTool
): ChatSegment[] => {
  const segments: ChatSegment[] = [];
  let index = 0;
  while (index < items.length) {
    const first = items[index];
    if (first.kind !== 'tool' || !groupsTool(first)) {
      segments.push({
        kind: 'item',
        key: first.id,
        item: first,
        afterCount: index + 1,
      });
      index += 1;
      continue;
    }
    const members: ToolGroupMember[] = [first];
    const tools: ToolItem[] = [first];
    let pending: AssistantItem[] = [];
    let lastIndex = index;
    for (let next = index + 1; next < items.length; next += 1) {
      if (hasEventsAt(eventsByIndex, next)) break;
      const item = items[next];
      if (
        item.kind === 'tool' &&
        item.toolName === first.toolName &&
        groupsTool(item)
      ) {
        members.push(...pending, item);
        tools.push(item);
        pending = [];
        lastIndex = next;
      } else if (isThinkingOnly(item)) {
        pending.push(item);
      } else {
        break;
      }
    }
    if (tools.length === 1) {
      segments.push({
        kind: 'item',
        key: first.id,
        item: first,
        afterCount: index + 1,
      });
      index += 1;
      continue;
    }
    segments.push({
      kind: 'toolGroup',
      key: first.id,
      toolName: first.toolName,
      members,
      tools,
      afterCount: lastIndex + 1,
    });
    index = lastIndex + 1;
  }
  return segments;
};

const PATH_TOOLS = new Set(['read', 'write', 'edit', 'ls', 'delete']);

export const toolGroupCount = (tools: ToolItem[]): number => {
  if (!tools.length || !PATH_TOOLS.has(tools[0].toolName)) return tools.length;
  const paths = new Set<string>();
  let unnamed = 0;
  for (const tool of tools) {
    const path = shortenWorkspacePaths((tool.argsSummary ?? '').trim());
    if (path) paths.add(path);
    else unnamed += 1;
  }
  return paths.size + unnamed;
};

const isActive = (status: ToolStatus) =>
  status === 'generating' || status === 'running';

export const toolGroupStatus = (tools: ToolItem[]): ToolStatus => {
  if (tools.some((tool) => tool.status === 'running')) return 'running';
  if (tools.some((tool) => tool.status === 'generating')) return 'generating';
  if (tools.some((tool) => tool.status === 'error')) return 'error';
  if (tools.length && tools.every((tool) => tool.status === 'cancelled')) {
    return 'cancelled';
  }
  return 'ok';
};

export const toolGroupLabel = (tools: ToolItem[], t: TFunction): string => {
  const count = toolGroupCount(tools);
  const one = count === 1;
  const active = tools.some((tool) => isActive(tool.status));
  const toolName = tools[0]?.toolName ?? '';
  const tool = tools[0]?.label ?? toolName;
  switch (toolName) {
    case 'edit':
      return active
        ? one
          ? t('Editing 1 file')
          : t('Editing {{count}} files', { count })
        : one
          ? t('Edited 1 file')
          : t('Edited {{count}} files', { count });
    case 'write':
      return active
        ? one
          ? t('Writing 1 file')
          : t('Writing {{count}} files', { count })
        : one
          ? t('Wrote 1 file')
          : t('Wrote {{count}} files', { count });
    case 'delete':
      return active
        ? one
          ? t('Deleting 1 file')
          : t('Deleting {{count}} files', { count })
        : one
          ? t('Deleted 1 file')
          : t('Deleted {{count}} files', { count });
    case 'read':
      return active
        ? one
          ? t('Reading 1 file')
          : t('Reading {{count}} files', { count })
        : one
          ? t('Read 1 file')
          : t('Read {{count}} files', { count });
    case 'ls':
      return active
        ? one
          ? t('Listing 1 directory')
          : t('Listing {{count}} directories', { count })
        : one
          ? t('Listed 1 directory')
          : t('Listed {{count}} directories', { count });
    case 'bash':
      return active
        ? one
          ? t('Running 1 command')
          : t('Running {{count}} commands', { count })
        : one
          ? t('Ran 1 command')
          : t('Ran {{count}} commands', { count });
    default:
      return active
        ? one
          ? t('Running {{tool}}', { tool })
          : t('Running {{tool}} ({{count}} calls)', { tool, count })
        : one
          ? t('{{tool}}', { tool })
          : t('{{tool}} ({{count}} calls)', { tool, count });
  }
};

export const toolGroupTarget = (tools: ToolItem[]): string =>
  toolArgsLabel(tools[tools.length - 1]?.argsSummary);
